/**
 * BudgetBuilder Calculations Router
 *
 * Worksheet creation always uses the experimental builder. Summary calculations
 * still route on the worksheet metadata version, since worksheets created before
 * the budget-builder rollout persist the 'legacy' version.
 *
 * Note: Legacy uses the original LaborModel/EquipmentModel for statistics calculations.
 * Only the worksheet builder and experimental statistics are Budget Builder-specific.
 */

import * as experimentalWorksheetBuilder from './experimental/worksheetBuilder'
import * as LaborModel from '../../laborModel'
import * as EquipmentModel from '../../equipmentModel'
import * as experimentalLaborStats from './experimental/laborStatistics'
import * as experimentalEquipmentStats from './experimental/equipmentStatistics'

import type {
  Worksheet,
  CompanyFormData,
  SeasonFormData,
  OwnerFormData,
  FieldEmployeesFormData,
  AdminEmployeesFormData,
  TrucksFormData,
  TrailersFormData,
  HeavyMachineryFormData,
  Employees,
  LaborStatistics,
  EquipmentList,
  EquipmentListWithStats,
  EquipmentStatistics
} from './types'

/**
 * Build a worksheet from BudgetBuilder form data
 */
export const buildWorksheet = (
  companyData: CompanyFormData,
  seasonData: SeasonFormData,
  ownerData: OwnerFormData,
  fieldEmployeesData: FieldEmployeesFormData,
  adminEmployeesData: AdminEmployeesFormData,
  trucksData: TrucksFormData,
  trailersData: TrailersFormData,
  heavyMachineryData: HeavyMachineryFormData
): Worksheet => {
  return experimentalWorksheetBuilder.buildWorksheet(
    companyData,
    seasonData,
    ownerData,
    fieldEmployeesData,
    adminEmployeesData,
    trucksData,
    trailersData,
    heavyMachineryData,
    'experimental'
  )
}

/**
 * Calculate labor statistics
 * Routes to experimental or standard (original) calculations based on worksheet version
 */
export const calculateLaborStatistics = (
  employees: Employees,
  version: 'legacy' | 'experimental'
): LaborStatistics => {
  return version === 'experimental'
    ? experimentalLaborStats.calculateLaborStatistics(employees)
    : LaborModel.calculateLaborStatistics(employees)
}

/**
 * Calculate equipment item statistics
 * Routes to experimental or standard (original) calculations based on worksheet version
 */
export const calculateItemStatistics = (
  equipmentList: EquipmentList,
  version: 'legacy' | 'experimental'
): EquipmentListWithStats => {
  return version === 'experimental'
    ? experimentalEquipmentStats.calculateItemStatistics(equipmentList)
    : EquipmentModel.calculateItemStatistics(equipmentList)
}

/**
 * Calculate overall equipment statistics
 * Routes to experimental or standard (original) calculations based on worksheet version
 */
export const calculateOverallStatistics = (
  equipmentListWithStats: EquipmentListWithStats,
  version: 'legacy' | 'experimental'
): EquipmentStatistics => {
  return version === 'experimental'
    ? experimentalEquipmentStats.calculateOverallStatistics(equipmentListWithStats)
    : EquipmentModel.calculateOverallStatistics(equipmentListWithStats)
}
